import { useFindAllDemosQuery } from '../../api/api-hooks';
import {
  DataGrid,
  GridColDef,
  GridRowParams,
  GridSelectionModel,
} from '@mui/x-data-grid';
import {
  differenceBetweenDateStringsInDays,
  hasDatePassed,
  truncateISOString,
} from '../../utils/dateTime';
import { CircularProgress } from '@mui/material';
import { Item } from '../../utils/sharedTypes';
import { Demo } from '../../api/api-generated';
import { DemosItemFilter } from './demosTypes';
import { filterDemoItem } from './demosUtils';
import { useHardwareTypeMap } from '../../hooks/useHardwareTypeMap';
import { useGridSort } from '../../hooks/useGridSort';

interface Props {
  setSelectedDemos: React.Dispatch<React.SetStateAction<Item[]>>;
  filters: DemosItemFilter;
  setEditModalOpenFlag: React.Dispatch<React.SetStateAction<boolean>>;
}

const DemosTable = ({
  setSelectedDemos,
  filters,
  setEditModalOpenFlag,
}: Props) => {
  const { data: demos } = useFindAllDemosQuery({});
  const typeMap = useHardwareTypeMap();
  const { sortModel, handleSortModelChange } = useGridSort('id', 'desc');
  if (demos === undefined || typeMap === undefined)
    return <CircularProgress></CircularProgress>;
  // Define data for display in DataGrid
  const columns: GridColDef[] = [
    { field: 'id', hide: true },
    { field: 'serial', headerName: 'Serial', width: 150 },
    { field: 'analyzerType', headerName: 'Analyzer Type', width: 170 },
    { field: 'firmware', headerName: 'Firmware', width: 140 },
    { field: 'companyName', headerName: 'Company', width: 150 },
    { field: 'contactName', headerName: 'Contact', width: 150 },
    { field: 'software', headerName: 'Software', width: 140 },
    { field: 'softwareVersion', headerName: 'Version', width: 130 },
    { field: 'shipDate', headerName: 'Ship Date', width: 140 },
    { field: 'startDate', headerName: 'Start', width: 140 },
    { field: 'endDate', headerName: 'End', width: 140 },
    { field: 'demoDuration', headerName: 'Duration', width: 135 },
    { field: 'demoStatus', headerName: 'Status', width: 120 },
  ];
  const rows = demos
    .filter((item: Demo) => filterDemoItem(item, filters))
    .map((item: Demo) => ({
      ...item,
      serial: item.inventoryHardware ? item.inventoryHardware.serial : 'None',
      analyzerType: item.inventoryHardware
        ? typeMap.get(item.inventoryHardware.hardwareTypeId)
        : 'None',
      companyName: item.company.companyName,
      software: item.software ? item.software.type : 'None',
      softwareVersion: item.softwareVersion
        ? item.softwareVersion.versionNumber
        : 'None',
      shipDate:
        item.shipDate === null ? 'NA' : truncateISOString(item.shipDate),
      demoDuration:
        item.endDate === null || item.startDate === null
          ? 0
          : differenceBetweenDateStringsInDays(item.endDate, item.startDate) +
            ' Days',
      startDate:
        item.startDate === null ? 'NA' : truncateISOString(item.startDate),
      endDate: item.endDate === null ? 'NA' : truncateISOString(item.endDate),
      demoStatus:
        item.endDate === null
          ? 'None'
          : hasDatePassed(new Date(item.endDate))
          ? 'Expired'
          : 'Active',
    }));
  const handleRowClick = (params: GridRowParams) => {
    setSelectedDemos(demos.filter((demo: Demo) => demo.id === params.row.id));
    setEditModalOpenFlag(true);
  };
  const handleTableSelection = (model: GridSelectionModel) => {
    setSelectedDemos(demos.filter((demo) => model.includes(demo.id)));
  };
  return (
    <div style={{ paddingBottom: '1rem', width: '100%' }}>
      <DataGrid
        rows={rows}
        columns={columns}
        pageSize={100}
        checkboxSelection
        disableSelectionOnClick
        autoHeight
        disableColumnMenu={true}
        onSelectionModelChange={handleTableSelection}
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        onRowClick={handleRowClick}
      />
    </div>
  );
};

export default DemosTable;
